import { isMediaStreamTrackProcessorSupported } from '../common/rtc-detection';
import { Logger } from '../index';
import loggerManager from './log/logger-manager';
import { IRoom, RoomEvent } from '../enums';
import { isUndefined } from './utils';

type StartConfig = {
  track: CanvasCaptureMediaStreamTrack | MediaStreamVideoTrack;
  isUplink: boolean;
  room: IRoom;
  userId: string;
  onBlack: () => void;
};

class BlackVideoDetector {
  worker: Worker | null;

  callbacks: Map<string, () => void> = new Map();
  _log: Logger;
  sleep: Record<string, number> = {};
  heartbeatListenerCleaner: Map<string, () => void> = new Map();
  userIdMap: Map<string, string> = new Map();

  constructor() {
    this._log = loggerManager.createLogger({ id: 'bvd' });
  }

  private getWorker() {
    if (!this.worker) {
      const workerCode = `
        const tracks=new Map;let canvas,ctx;const log=(...t)=>postMessage({type:"log",message:"[worker] "+t.join(" ")});function startDetection(e,t,a){if(!tracks.has(e)){const c={reader:a.getReader(),blackCount:0,timeoutId:null,intervalId:null};tracks.set(e,c),c.timeoutId=setTimeout(()=>stopDetection(e,"timeout"),t),c.intervalId=setInterval(async()=>{try{await isFrameBlack(e)?(c.blackCount++,postMessage({type:"blackCount",trackId:e,count:c.blackCount}),3<=c.blackCount&&(postMessage({type:"black",trackId:e}),stopDetection(e,"black"))):c.blackCount=0}catch(t){log("check black video error:",t.message),stopDetection(e,"error")}},1e3)}}function stopDetection(t,e){var a=tracks.get(t);a&&(a.timeoutId&&clearTimeout(a.timeoutId),a.intervalId&&clearInterval(a.intervalId),a.reader&&a.reader.cancel(),tracks.delete(t),postMessage({type:e,trackId:t}))}async function isFrameBlack(t){t=tracks.get(t);if(!t)return!1;var t=t.reader,{done:t,value:e}=await t.read();if(!e||t)return!1;canvas||(canvas=new OffscreenCanvas(e.codedWidth,e.codedHeight),ctx=canvas.getContext("2d",{willReadFrequently:!0})),canvas.width===e.codedWidth&&canvas.height===e.codedHeight||(canvas.width=e.codedWidth,canvas.height=e.codedHeight,ctx=canvas.getContext("2d",{willReadFrequently:!0})),ctx.drawImage(e,0,0,canvas.width,canvas.height);t=getFrameBlackRatio(ctx.getImageData(0,0,canvas.width,canvas.height));return e.close(),1===t}function getFrameBlackRatio(t){var e=t.data;let a=0;for(let t=0;t<100;t++){var c=4*Math.floor(Math.random()*(e.length/4)),[c,r,n,o]=[e[c],e[1+c],e[2+c],e[3+c]];0<o&&0===c&&0===r&&0===n&&a++}return a/100}self.onmessage=t=>{var{type:t,trackId:e,timeout:a,readable:c}=t.data;"addTrack"===t&&startDetection(e,a,c),"removeTrack"===t&&stopDetection(e)};
      `;
      const workerBlob = new Blob([workerCode], { type: 'application/javascript' });

      const workerUrl = URL.createObjectURL(workerBlob);
      this.worker = new Worker(workerUrl);
      URL.revokeObjectURL(workerUrl);

      this.worker.onerror = error => this._log.warn('worker error:', error.message, error.filename || 'unknown', error.lineno || 'unknown');
      this.worker.onmessage = event => {
        const { type, trackId, message, count } = event.data;
        if (type === 'black') {
          this.callbacks.get(trackId)?.();
        } else if (type === 'log') {
          this._log.warn(message);
        } else if (type === 'blackCount') {
          const userId = this.userIdMap.get(trackId);
          this._log.warn(`${userId || trackId} black count: ${count}`);
        }
      };
    }
    return this.worker;
  }

  start({ track, isUplink, room, userId, onBlack }: StartConfig): string | undefined {
    this._log.debug('start detect black video', track.id);
    if (!isMediaStreamTrackProcessorSupported() || !onBlack || !track || typeof Worker === 'undefined') {
      this._log.warn('black video detector not supported');
      return;
    }

    const heartbeatListener = (report: HearBeatReport) => {
      let smallVideo;
      if (isUplink) {
        // 上行：直接从 msg_up_stream_info 获取
        smallVideo = report.msg_up_stream_info?.msg_video_status?.filter(item => item.uint32_video_stream_type === 3)[0];
      } else {
        // 下行：先过滤用户，再获取 video status
        const userStreamInfo = report.msg_down_stream_info?.filter(item => item.msg_user_info?.str_identifier === userId)[0];
        smallVideo = userStreamInfo?.msg_video_status?.filter(item => item.uint32_video_stream_type === 3)[0];
      }
      if (smallVideo) {
        const bitrate = (smallVideo.uint32_video_codec_bitrate || 0) / 1000;
        if (this.sleep[track.id] && this.sleep[track.id] > 0) {
          this.sleep[track.id] -= 1;
          return;
        }
        if (bitrate > 0 && bitrate < 10) {
          this.sleep[track.id] = 30;
          this._log.info('track bitrate', bitrate, 'start check');
          this.checkOnce(track, 30 * 1000);
        }
      }
    };
    room.on(RoomEvent.HEARTBEAT_REPORT, heartbeatListener);
    this.heartbeatListenerCleaner.set(track.id, () => room.off(RoomEvent.HEARTBEAT_REPORT, heartbeatListener));
    this.callbacks.set(track.id, onBlack);
    this.userIdMap.set(track.id, userId);

    return track.id;
  }

  checkOnce(track: CanvasCaptureMediaStreamTrack | MediaStreamVideoTrack, timeout: number) {
    try {
      const worker = this.getWorker();
      if (!worker) throw new Error('Worker not available');
      const processor = new MediaStreamTrackProcessor({ track: track as MediaStreamVideoTrack });
      worker.postMessage({ type: 'addTrack', trackId: track.id, timeout, readable: processor.readable }, [processor.readable]);
    } catch (error) {
      this._log.warn('check error:', error);
      this.stop(track.id);
    }
  }

  stop(trackId?: string) {
    if (trackId) {
      if (this.worker) {
        this.worker.postMessage({ type: 'removeTrack', trackId });
      }
      this.callbacks.delete(trackId);
      delete this.sleep[trackId];
      const cleaner = this.heartbeatListenerCleaner.get(trackId);
      if (cleaner) {
        cleaner();
      }
      this.heartbeatListenerCleaner.delete(trackId);
      this.userIdMap.delete(trackId);
    }
  }

  destroy() {
    this.callbacks.forEach((callback, trackId) => this.stop(trackId));
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }
}

export default new BlackVideoDetector();

// worker 源码，压缩工具 https://skalman.github.io/UglifyJS-online/
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const workerSource = `
  const tracks = new Map();
  let canvas, ctx;

  const log = (...args) => postMessage({ type: 'log', message: '[worker] ' + args.join(' ') });

  self.onmessage = (event) => {
    const { type, trackId, timeout, readable } = event.data;
    if (type === 'addTrack') startDetection(trackId, timeout, readable);
    if (type === 'removeTrack') stopDetection(trackId);
  };

  function startDetection(trackId, timeout, readable) {
    if (tracks.has(trackId)) return;
    const state = {
      reader: readable.getReader(),
      blackCount: 0,
      timeoutId: null,
      intervalId: null
    };
    tracks.set(trackId, state);
    
    state.timeoutId = setTimeout(() => stopDetection(trackId, 'timeout'), timeout);
    state.intervalId = setInterval(async () => {
      try {
        const isBlack = await isFrameBlack(trackId);
        if (!isBlack) {
          state.blackCount = 0;
          return;
        }
        state.blackCount++;
        postMessage({ type: 'blackCount', trackId, count: state.blackCount });
        if (state.blackCount >= 3) {
          postMessage({ type: 'black', trackId });
          stopDetection(trackId, 'black');
        }
      } catch (error) {
        log('check black video error:', error.message);
        stopDetection(trackId, 'error');
      }
    }, 1000);
  }

  function stopDetection(trackId, type) {
    const state = tracks.get(trackId);
    if (!state) return;
    if (state.timeoutId) clearTimeout(state.timeoutId);
    if (state.intervalId) clearInterval(state.intervalId);
    if (state.reader) state.reader.cancel();
    tracks.delete(trackId);
    postMessage({ type, trackId });
  }

  async function isFrameBlack(trackId) {
    const state = tracks.get(trackId);
    if (!state) return false;
    
    const { reader } = state;
    const { done, value } = await reader.read();
    if (!value || done) return false;
    
    if (!canvas) {
      canvas = new OffscreenCanvas(value.codedWidth, value.codedHeight);
      ctx = canvas.getContext('2d', { willReadFrequently: true });
    }
    if (canvas.width !== value.codedWidth || canvas.height !== value.codedHeight) {
      canvas.width = value.codedWidth;
      canvas.height = value.codedHeight;
      ctx = canvas.getContext('2d', { willReadFrequently: true });
    }

    ctx.drawImage(value, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const blackRatio = getFrameBlackRatio(imageData);
    
    value.close();
    return blackRatio === 1;
  }

  function getFrameBlackRatio(imageData) {
    const { data } = imageData;
    const sampleSize = 100;
    let blackPixels = 0;
    for (let i = 0; i < sampleSize; i++) {
      const randomIndex = Math.floor(Math.random() * (data.length / 4)) * 4;
      const [r, g, b, a] = [data[randomIndex], data[randomIndex + 1], data[randomIndex + 2], data[randomIndex + 3]];
      if (a > 0 && r === 0 && g === 0 && b === 0) {
        blackPixels++;
      }
    }
    return blackPixels / sampleSize;
  }
`;

